import S from 's-js';

import { CachedModelLoader } from 'lib/CachedModelLoader';

import { CoordinationCard } from 'data/models/CoordinationCard';
import { CoordinationCardDetailsResponse, CoordinationCardDetailsResponseOptions } from 'data/models/CoordinationCardDetailsResponse';
import { CoordinationCardRights } from 'server/enums/Rights';

import { PCCache } from 'data/PCCache';

import { PCTabStrip, TitledTab } from 'portcall/pctabstrip/model';

import {
    CoordinationCardDetailsActionsTab as ActionsTabModel,
    CoordinationCardDetailsActionsTabParams as ActionsTabParams
} from './actions';
import {
    CoordinationCardDetailsLogTab as LogTabModel,
    CoordinationCardDetailsLogTabParams as LogTabParams
} from './log';
import {
    CoordinationCardDetailsStatusTab as StatusTabModel,
    CoordinationCardDetailsStatusTabParams as StatusTabParams
} from './status';

export const TabLog      : TitledTab = { Title: 'Log'      };
export const TabActions  : TitledTab = { Title: 'Actions'  };
export const TabStatus   : TitledTab = { Title: 'Status'   };

export interface CoordinationCardDetailsParams {
    CoordinationCard : CoordinationCard;
    DismissDetails : () => void;
    ShouldPauseLogUpdates? : () => boolean;
}

export type CoordinationCardDetails = ReturnType<typeof CoordinationCardDetails>;
export function CoordinationCardDetails(
    loader : CachedModelLoader<CoordinationCardDetailsResponse, CoordinationCardDetailsResponseOptions>,
    data : PCCache,
    statusTabModel : (params : StatusTabParams) => StatusTabModel,
    actionsTabModel : (params : ActionsTabParams) => ActionsTabModel,
    logTabModel : (params : LogTabParams) => LogTabModel,
    {
        CoordinationCard,
        DismissDetails,
        ShouldPauseLogUpdates
    } : CoordinationCardDetailsParams
) {
    const
        { model: detailsResp, loading: detailsRespLoading } = loader({ Id: CoordinationCard.Id() }, 10),
        // Upsert Self ourselves so it doesn't matter whether server has ever sent the Self object
        // to us or not.
        Self = data.CoordinationCardSelves.update1(null, { Id: CoordinationCard.Id() }),
        StatusTab = statusTabModel({
            CoordinationCard,
            DismissDetails,
            HasEditStatus: () => (detailsResp.Rights() & CoordinationCardRights.EditStatus) !== 0
        }),
        ActionsTab = actionsTabModel({
            CoordinationCard,
            DismissDetails,
            Rights: detailsResp.Rights
        }),
        LogTab = logTabModel({ CoordinationCardSelf: Self }),
        // NOTE: We'll keep the Actions tab available always, even if there are no individual actions available,
        // b/c it's also the main/only place where non-admins can view the coordination description.
        TabStrip = PCTabStrip(TabStatus, () => [TabActions, TabLog]),
        Loading = () =>
            detailsRespLoading()
         || StatusTab.Loading()
         || ActionsTab.Loading()
         || LogTab.Loading();

    // Turn log updater on/off on various triggers...
    S(() => {
        // Basic logic -- run log updates as long as we're on the log tab, or we can get a head start if we
        // know we're flipping to the log tab...
        const
            tabState = TabStrip.State(),
            onOrApproachingLogTab =
                tabState.state === 'settled-on-back'  && tabState.tab   === TabLog ||
                tabState.state === 'flipping-to-back' && tabState.toTab === TabLog,
            // ...but also give calling code a say in whether we should be running log updates.
            shouldRunLogUpdates = onOrApproachingLogTab && !ShouldPauseLogUpdates?.();

        if (shouldRunLogUpdates) LogTab.LogUpdater.start();
        else LogTab.LogUpdater.stop();
    });

    return {
        DismissDetails,
        StatusTab,
        ActionsTab,
        LogTab,
        TabStrip,
        Loading
    };
}



// WEBPACK FOOTER //
// ./apps/portcall/coordinationcarddetails/model.ts